import styled from '@emotion/styled';
import { setNewOrderId, setStep, toggleCancelationDialog, clearOrderPlacement } from '@/redux/slices/orderPlacementSlice';
import { Button, Container, Grid, Paper, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import Confetti from 'react-confetti';
import confettiImg from '../../assets/images/confetti.png';
import { selectNewOrderId } from '@/redux/selectors/orderPlacementSelectors';
import { clearTitle } from '@/redux/slices/titleSlice';
import PolarisIcon from '../_common/PolarisIcon';
import LinkButton from '../_common/LinkButton';
import { clearProfiles } from '@/redux/slices/profileSlice';
import { useFile } from '@/contextAPI/fileContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useGoToCreatedOrder } from '@/components/OrderPlacement/hooks/useGoToCreatedOrder.hook';
import { clearCache } from '@/redux/slices/titleV2Slice';

type OPOrderConfirmationProps = {
  isBulkBased: boolean;
};

const OPOrderConfirmation = ({ isBulkBased }: OPOrderConfirmationProps) => {
  const dispatch = useAppDispatch();
  const { setFile } = useFile();
  const newOrderId = useAppSelector(selectNewOrderId);
  const wrapper = useRef<HTMLDivElement>(null);
  const wrapperOffsetWidth = wrapper?.current?.offsetWidth || 0;
  const wrapperOffsetHeight = wrapper?.current?.offsetHeight || 0;
  const [party, setParty] = useState(false);
  const goToCreatedOrder = useGoToCreatedOrder();

  const createNewOrder = () => {
    dispatch(clearOrderPlacement());
    dispatch(clearTitle());
    dispatch(clearCache());
    dispatch(toggleCancelationDialog(false));
    dispatch(setStep(0));
    dispatch(clearProfiles());
    setFile({});
  };

  useEffect(() => {
    setParty(true);
    return () => {
      dispatch(clearTitle());
      dispatch(clearCache());
      dispatch(clearOrderPlacement());
      dispatch(setNewOrderId(null));
      dispatch(setStep(0));
    };
  }, []);

  const handleViewOrderButtonClick = () => {
    if (!newOrderId) {
      throw new Error('New order id is not set');
    }

    goToCreatedOrder(newOrderId);
  };

  return (
    <div ref={wrapper}>
      <Container sx={{ mt: 5 }}>
        <Grid container display="flex" direction="column" alignItems="center">
          <StyledPaperTopSection sx={{ width: 400, height: 600, flexGrow: 1 }}>
            <StyledContainer container display="flex" direction="column" alignItems="center" justifyContent="center">
              <PartyIcon src={confettiImg} alt="Order created successfully" style={{ marginBottom: 50 }} onClick={() => setParty(true)} />
              <Typography sx={{ mb: 1 }} fontSize="1.4rem" variant="h4">
                <strong>Order #{newOrderId}</strong>
              </Typography>
              <StyledMessageTypography variant="subtitle1">{`Your ${isBulkBased ? 'bulk ' : ''}order has been successfully ${
                isBulkBased ? 'created' : 'submitted'
              }!`}</StyledMessageTypography>

              <Button
                variant="contained"
                color="primary"
                onClick={handleViewOrderButtonClick}
                endIcon={<StyledPolarisIcon icon="right" height={25} />}
              >
                View order
              </Button>
            </StyledContainer>
          </StyledPaperTopSection>
          <StyledPaperBottomSection
            sx={{
              maxWidth: 450,
              height: 50,
              flexGrow: 1
            }}
          >
            <Grid container display="flex" justifyContent="center" height="100%">
              <LinkButton onClick={createNewOrder} sx={{ fontSize: '1rem', color: '#fff', width: '100%' }}>
                Create New Order
              </LinkButton>
            </Grid>
          </StyledPaperBottomSection>
          <StyledConfetti
            width={wrapperOffsetWidth - 100}
            height={wrapperOffsetHeight + 100}
            numberOfPieces={party ? 200 : 0}
            recycle={false}
            onConfettiComplete={(confetti) => {
              confetti?.reset();
              setParty(false);
            }}
            confettiSource={{
              x: 100,
              y: 0,
              w: wrapperOffsetWidth - 100,
              h: wrapperOffsetHeight + 100
            }}
          />
        </Grid>
      </Container>
    </div>
  );
};

const StyledPaperTopSection = styled(Paper)`
  z-index: 100;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;

const StyledPaperBottomSection = styled(Paper)`
  background-color: ${(props) => props.theme.palette.primary.dark};
  width: 400px;
  z-index: 100;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
`;

const StyledConfetti = styled(Confetti)`
  margin-top: ${(props) => props.theme.spacing(35)};
  margin-left: auto;
  margin-right: auto;
`;

const StyledContainer = styled(Grid)`
  height: 100%;
  padding: ${(props) => props.theme.spacing(10)};
  text-align: center;
`;

const StyledMessageTypography = styled(Typography)`
  color: ${(props) => props.theme.palette.default.main};
  font-size: 1.4rem;
  margin-bottom: ${(props) => props.theme.spacing(3)};
  font-weight: initial;
`;

const StyledPolarisIcon = styled(PolarisIcon)`
  margin-left: ${(props) => props.theme.spacing(0.5)};
`;

const PartyIcon = styled.img`
  cursor: pointer;
  transform: scale(1);
  transition: transform 0.3s;
  &:hover {
    transform: scale(1.1);
    transition: transform 0.3s;
  }
`;

export default OPOrderConfirmation;
